import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Loader2 } from 'lucide-react';
import HtmlDiff from 'htmldiff-js';
import DOMPurify from 'dompurify';
import { getArticleRevision } from '../../../services/api';
import type { ArticleRevisionMeta } from '@/types';

interface CompositeSource {
  title: string;
  description: string;
  content_html: string;
}

interface VersionDiffDialogProps {
  helpCenterId: string;
  itemId: string;
  revisions: ArticleRevisionMeta[];
  /** The current (in-editor) content — the "Current version" comparison target. */
  currentTitle: string;
  currentDescription: string;
  currentHtml: string;
  initialFromId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CURRENT = 'current';

const escapeHtml = (s: string) =>
  (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Compose title + description + body into one HTML doc so title/description changes
// are captured by the diff alongside body content.
const buildComposite = (src: CompositeSource) => {
  const parts = [`<h1>${escapeHtml(src.title)}</h1>`];
  if (src.description) {
    parts.push(`<p><em>${escapeHtml(src.description)}</em></p>`);
  }
  parts.push(src.content_html || '');
  return parts.join('\n');
};

const fmt = (s: string) => {
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
};

export const VersionDiffDialog: React.FC<VersionDiffDialogProps> = ({
  helpCenterId,
  itemId,
  revisions,
  currentTitle,
  currentDescription,
  currentHtml,
  initialFromId,
  open,
  onOpenChange,
}) => {
  const [fromId, setFromId] = useState<string>(initialFromId || revisions[0]?.id || '');
  const [toId, setToId] = useState<string>(CURRENT);
  const [loading, setLoading] = useState(false);
  const [diffHtml, setDiffHtml] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cacheRef = useRef<Record<string, CompositeSource>>({});

  useEffect(() => {
    if (open) {
      setFromId(initialFromId || revisions[0]?.id || '');
      setToId(CURRENT);
    }
  }, [open, initialFromId, revisions]);

  const getSource = useCallback(
    async (id: string): Promise<CompositeSource> => {
      if (id === CURRENT) {
        return { title: currentTitle, description: currentDescription, content_html: currentHtml };
      }
      if (cacheRef.current[id]) return cacheRef.current[id];
      const res = await getArticleRevision(helpCenterId, itemId, id);
      if (!res.ok || !res.revision) throw new Error(res.error || 'Failed to load version');
      const src: CompositeSource = {
        title: res.revision.title || '',
        description: res.revision.description || '',
        content_html: res.revision.content_html || '',
      };
      cacheRef.current[id] = src;
      return src;
    },
    [helpCenterId, itemId, currentTitle, currentDescription, currentHtml]
  );

  useEffect(() => {
    if (!open || !fromId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [fromSrc, toSrc] = await Promise.all([getSource(fromId), getSource(toId)]);
        if (cancelled) return;
        setDiffHtml(HtmlDiff.execute(buildComposite(fromSrc), buildComposite(toSrc)));
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Failed to build diff');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, fromId, toId, getSource]);

  const selectCls =
    'h-8 max-w-[45%] truncate rounded-md border bg-background px-2 text-sm';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Compare versions</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-muted-foreground">From</span>
          <select
            className={selectCls}
            value={fromId}
            onChange={(e) => setFromId(e.target.value)}
          >
            {revisions.map((r) => (
              <option key={r.id} value={r.id}>
                {fmt(r.created_at)}
                {r.author ? ` · ${r.author}` : ''}
              </option>
            ))}
          </select>
          <span className="text-muted-foreground">to</span>
          <select className={selectCls} value={toId} onChange={(e) => setToId(e.target.value)}>
            <option value={CURRENT}>Current version</option>
            {revisions.map((r) => (
              <option key={r.id} value={r.id}>
                {fmt(r.created_at)}
                {r.author ? ` · ${r.author}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded-sm"
              style={{ background: 'rgba(34,197,94,0.35)' }}
            />
            Added
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded-sm"
              style={{ background: 'rgba(239,68,68,0.35)' }}
            />
            Removed
          </span>
        </div>

        <div className="max-h-[60vh] overflow-y-auto rounded-md border p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-destructive">{error}</div>
          ) : (
            <div className="diff-view tiptap-editor" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(diffHtml) }} />
          )}
        </div>

        <style>{`
          .diff-view ins, .diff-view ins.diffins, .diff-view .diffmod {
            background: rgba(34,197,94,0.2); text-decoration: none; border-radius: 2px;
          }
          .diff-view del, .diff-view del.diffdel {
            background: rgba(239,68,68,0.2); text-decoration: line-through; border-radius: 2px;
          }
          .diff-view ins img { outline: 2px solid rgba(34,197,94,0.55); }
          .diff-view del img { outline: 2px solid rgba(239,68,68,0.55); opacity: .6; }
        `}</style>
      </DialogContent>
    </Dialog>
  );
};

export default VersionDiffDialog;
